import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatOption, MatSelect } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatStep, MatStepLabel, MatStepper, MatStepperIcon, MatStepperNext, MatStepperPrevious } from '@angular/material/stepper';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, finalize, switchMap, tap, throwError } from 'rxjs';

import { Client } from '../../../../models/clients.model';
import {
  UnitResponseModel,
} from '../../../units/units.model';
import { UnitService } from '../../../units/services/unit.service';
import { ClientsService } from '../../services/clients.service';
import {
  ContactResolution,
  MovePreviewContact,
  MovePreviewResponse,
} from '../../models/move-client.models';

interface ContactRow extends MovePreviewContact {
  action: 'replace' | 'import';
  targetContactId?: string;
}

@Component({
  selector: 'app-move-client-wizard',
  standalone: true,
  imports: [
    MatStepper,
    MatStep,
    MatStepLabel,
    MatStepperIcon,
    MatStepperNext,
    MatStepperPrevious,
    MatButton,
    MatIconButton,
    MatIcon,
    MatFormField,
    MatLabel,
    MatSelect,
    MatOption,
    FormsModule,
  ],
  templateUrl: './move-client-wizard.component.html',
  styleUrl: './move-client-wizard.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MoveClientWizardComponent implements OnInit {
  private readonly _route = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _clients = inject(ClientsService);
  private readonly _units = inject(UnitService);
  private readonly _snackbar = inject(MatSnackBar);

  // State
  protected readonly client = signal<Client | null>(null);
  protected readonly units = signal<UnitResponseModel[]>([]);
  protected readonly selectedUnitId = signal<string | null>(null);
  protected readonly preview = signal<MovePreviewResponse | null>(null);
  protected readonly contactRows = signal<ContactRow[]>([]);
  protected readonly isLoadingPreview = signal(false);
  protected readonly isSubmitting = signal(false);
  protected readonly maxStepReached = signal(0);

  @ViewChild(MatStepper) stepper!: MatStepper;

  // Derived
  protected readonly availableUnits = computed(() => {
    const c = this.client();
    if (!c) return this.units();
    return this.units().filter((u) => u._id !== c.unitId && !u.deleteAt);
  });

  protected readonly sourceUnit = computed(() => {
    const c = this.client();
    if (!c) return null;
    return this.units().find((u) => u._id === c.unitId) ?? null;
  });

  protected readonly selectedUnit = computed(() => {
    const id = this.selectedUnitId();
    return this.units().find((u) => u._id === id) ?? null;
  });

  protected readonly hasContacts = computed(() => {
    const p = this.preview();
    return p != null && p.contactsInUse.length > 0;
  });

  protected readonly isContactStepValid = computed(() => {
    // Guard: if preview hasn't loaded yet, step is not "complete"
    if (!this.preview()) return false;
    const rows = this.contactRows();
    return rows.every(
      (r) => r.action === 'import' || (r.action === 'replace' && r.targetContactId)
    );
  });

  ngOnInit(): void {
    const clientId = this._route.snapshot.paramMap.get('clientId');
    if (!clientId) {
      this._router.navigate(['/clients']);
      return;
    }

    // Load client and units in parallel
    this._clients.getById(clientId).subscribe({
      next: (client) => {
        this.client.set(client);

        if (client.moveId) {
          this._snackbar.open('Deze cliënt wordt al verplaatst', 'OK');
          this._router.navigate(['/clients']);
        }
      },
      error: () => {
        this._snackbar.open('Cliënt niet gevonden', 'OK');
        this._router.navigate(['/clients']);
      },
    });

    this._units.getByUser().subscribe({
      next: (units) => this.units.set(units),
    });
  }

  onUnitSelected(): void {
    const clientId = this.client()?._id;
    const unitId = this.selectedUnitId();
    if (!clientId || !unitId) return;

    this.isLoadingPreview.set(true);
    this.preview.set(null);
    this.contactRows.set([]);

    this._clients
      .getMovePreview(clientId, unitId)
      .pipe(finalize(() => this.isLoadingPreview.set(false)))
      .subscribe({
        next: (preview) => {
          this.preview.set(preview);
          this.contactRows.set(
            preview.contactsInUse.map((c) => {
              const match = preview.targetUnitContacts.find(
                (t) =>
                  t.name === c.name &&
                  t.phoneNumber === c.phoneNumber &&
                  t.email === c.email
              );
              return match
                ? { ...c, action: 'replace' as const, targetContactId: match._id }
                : { ...c, action: 'import' as const };
            })
          );
        },
        error: () => {
          this._snackbar.open(
            'Er is iets misgegaan bij het laden van het voorbeeld',
            'OK'
          );
        },
      });
  }

  onContactActionChange(index: number, action: 'replace' | 'import'): void {
    this.contactRows.update((rows) => {
      const updated = [...rows];
      updated[index] = { ...updated[index], action, targetContactId: undefined };
      return updated;
    });
  }

  onTargetContactChange(index: number, targetContactId: string): void {
    this.contactRows.update((rows) => {
      const updated = [...rows];
      updated[index] = { ...updated[index], targetContactId };
      return updated;
    });
  }

  goToStep(step: number): void {
    if (this.stepper) {
      this.stepper.selectedIndex = step;
    }
  }

  onStepChange(event: StepperSelectionEvent): void {
    this.maxStepReached.update((current) => Math.max(current, event.selectedIndex));
  }

  submit(): void {
    const clientId = this.client()?._id;
    const targetUnitId = this.selectedUnitId();
    if (!clientId || !targetUnitId) return;

    const contactResolutions: ContactResolution[] = this.contactRows().map((r) => ({
      sourceContactId: r._id,
      action: r.action,
      ...(r.action === 'replace' && r.targetContactId
        ? { targetContactId: r.targetContactId }
        : {}),
    }));

    this.isSubmitting.set(true);

    this._clients
      .moveClient(clientId, { targetUnitId, contactResolutions })
      .pipe(
        tap(() => {
          this._snackbar.open(
            `Cliënt ${this.client()?.name} wordt verplaatst...`,
            'OK'
          );
          this._router.navigate(['/clients']);
        }),
        catchError((error) => {
          this._snackbar.open(
            'Er is iets misgegaan bij het verplaatsen. Probeer het later nog eens.',
            'OK'
          );
          return throwError(() => error);
        }),
        finalize(() => this.isSubmitting.set(false))
      )
      .subscribe();
  }

  getTargetContactName(targetContactId?: string): string {
    if (!targetContactId) return '—';
    const contact = this.preview()?.targetUnitContacts.find(
      (c) => c._id === targetContactId
    );
    return contact?.name ?? '—';
  }

  cancel(): void {
    this._router.navigate(['/clients']);
  }
}
